import {call, delay, put, race, select, take, takeLatest} from "redux-saga/effects";

import * as UserActions from "@atg-global-shared/user/userActions";
import {AuthActions} from "@atg-shared/auth";
import log, {serializeError} from "@atg-shared/log";
import {TillsammansAnalytics} from "@atg-tillsammans/analytics";
import {GameUtils} from "@atg-tillsammans/game-utils";
import {TeamSelectors} from "@atg-tillsammans/team-data-access/redux/team";
import {TeamService} from "@atg-tillsammans/team-data-access/services";
import {
    TeamMemberActions,
    TeamMemberSelectors,
} from "@atg-tillsammans/team-member-data-access";
import {ToastContainerId} from "@atg-tillsammans/toasts-types";
import {TeamInviteStatus, type RoundErrorCode} from "@atg-tillsammans/types";
import {ApplicationUtils} from "@atg-tillsammans/utils";
import {
    PersonalizationSelectors,
    PersonalizationActions,
} from "@atg-tillsammans-shared/personalization-data-access";
import * as ToastActions from "atg-ui-toast/domain/toastActions";

import {RoundService} from "../services";

import {
    PURCHASE_CONFIRM,
    PURCHASE_STARTED,
    RoundPurchaseActions,
    type RoundPurchaseConfirmAction,
} from "./round.actions";
import {RoundPurchaseModalType} from "./round.types";

import type {ApolloError} from "@apollo/client";
import type {Participant} from "@atg-tillsammans/types/generated";
import type {SagaIterator} from "redux-saga";

export const MEMBER_ID_WAIT_TIMEOUT_MS = 10_000;

function* waitUntilMemberIdReceived(): SagaIterator<number> {
    while (true) {
        yield take([
            PersonalizationActions.RECEIVED_PERSONALIZATION_SETTINGS,
            PersonalizationActions.RECEIVED_PERSONALIZATION_SETTINGS_PUSH,
        ]);
        const memberId: number | undefined = yield select(
            PersonalizationSelectors.getMemberId,
        );
        if (memberId) return memberId;
    }
}

export function* waitForMemberId(): SagaIterator<number | undefined> {
    const memberId: number | undefined = yield select(
        PersonalizationSelectors.getMemberId,
    );
    if (memberId) return memberId;

    const {receivedMemberId, timeout} = yield race({
        receivedMemberId: call(waitUntilMemberIdReceived),
        timeout: delay(MEMBER_ID_WAIT_TIMEOUT_MS),
    });

    if (receivedMemberId) return receivedMemberId;

    const memberIdAfterTimeout: number | undefined = yield select(
        PersonalizationSelectors.getMemberId,
    );
    if (timeout && !memberIdAfterTimeout) {
        log.warn("Timed out waiting for memberId after login");
    }

    return memberIdAfterTimeout;
}

/**
 * @param action: RoundPurchaseAction
 * @returns
 */
export function* initPurchase(): SagaIterator {
    yield put(AuthActions.checkAuth(true));

    const authResult = yield take(AuthActions.AUTH_CHECK_RESPONSE);
    if (authResult.error) {
        return;
    }

    const memberId = yield call(waitForMemberId);

    if (memberId) {
        yield put(RoundPurchaseActions.setPurchaseModal(RoundPurchaseModalType.CONFIRM));
    }
}

/**
 * @param action: RoundPurchaseConfirmAction
 * @returns
 */
export function* confirmPurchase(action: RoundPurchaseConfirmAction): SagaIterator {
    yield put(AuthActions.checkAuth(true));

    const authResult = yield take(AuthActions.AUTH_CHECK_RESPONSE);

    if (authResult.error) {
        return;
    }

    const isTeamMember = yield select(
        TeamMemberSelectors.isTeamMember(action.payload.input.teamId),
    );

    if (!isTeamMember) {
        try {
            const [status, errors] = yield call(TeamService.joinTeam, {
                teamId: action.payload.input.teamId,
                greeting: null,
            });

            const success =
                status === TeamInviteStatus.JOINED ||
                status === TeamInviteStatus.ALREADY_MEMBER;

            if (success) {
                yield put(TeamMemberActions.fetchMemberTeams({refreshTeams: true}));
                yield put(
                    ToastActions.showToast({
                        containerId: ToastContainerId.TEAM_PAGE,
                        type: "success",
                        message: ApplicationUtils.getCreateSuccessMessage(status),
                        timeout: 10000,
                        reason: status,
                    }),
                );
            }

            if (!success || errors) {
                yield put(RoundPurchaseActions.purchaseFinished());
                return;
            }
        } catch (error: unknown) {
            log.error("confirmPurchase error", {error: serializeError(error)});
            yield put(RoundPurchaseActions.purchaseFinished());
            return;
        }
    }

    try {
        const result: {participation?: Participant; error?: RoundErrorCode} = yield call(
            RoundService.createParticipation,
            {
                input: action.payload.input,
            },
            action.payload.salesChannel,
        );

        if (result?.participation) {
            yield put(
                RoundPurchaseActions.purchaseSuccess({
                    participant: result.participation,
                    roundId: action.payload.roundId,
                }),
            );
            yield call(TillsammansAnalytics.trackPurchaseShareEvent, {
                cost: result.participation.totalCost,
                name: GameUtils.getGameType(result.participation.gameId),
                variant: "andel",
                category: GameUtils.isSportGame(result.participation.gameId)
                    ? "sport"
                    : "trav",
                id: result.participation.id,
            });
            yield put(
                RoundPurchaseActions.setPurchaseModal(RoundPurchaseModalType.SUCCESS),
            );
            yield put(UserActions.fetchBalance());
        }

        if (result?.error) {
            yield put(
                RoundPurchaseActions.purchaseError({
                    roundId: action.payload.roundId,
                    errorCode: result.error,
                }),
            );
        }
    } catch (error: unknown) {
        yield put(
            RoundPurchaseActions.purchaseError({
                roundId: action.payload.roundId,
                errorCode: (error as ApolloError)?.graphQLErrors[0].extensions
                    ?.errorCode as RoundErrorCode,
            }),
        );
        log.error("confirmPurchase error", {error: serializeError(error)});
    }

    yield put(RoundPurchaseActions.purchaseFinished());
}

export default function* roundSaga() {
    yield takeLatest(PURCHASE_STARTED, initPurchase);
    yield takeLatest(PURCHASE_CONFIRM, confirmPurchase);
}
